import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { TrendingUp, TrendingDown, Minus, X, ImagePlus } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface UserPostComposerProps {
  open: boolean
  onClose: () => void
  onSubmit: (insight: {
    content: string
    ticker?: string
    tags: string[]
    sentiment: 'bullish' | 'neutral' | 'bearish'
    imageUrl?: string
  }) => void
}

export function UserPostComposer({
  open,
  onClose,
  onSubmit,
}: UserPostComposerProps) {
  const { t } = useTranslation()
  const [content, setContent] = useState('')
  const [ticker, setTicker] = useState('')
  const [tags, setTags] = useState('')
  const [sentiment, setSentiment] = useState<
    'bullish' | 'neutral' | 'bearish' | null
  >(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handlePost = useCallback(() => {
    // Validation
    if (!content.trim()) {
      toast.error(t('contentRequired'), {
        description: t('pleaseWriteInsight'),
      })
      return
    }

    if (!sentiment) {
      toast.error(t('sentimentRequired'), {
        description: t('selectSentimentDesc'),
      })
      return
    }

    // Parse tags (comma-separated)
    const parsedTags = tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0)

    // Submit insight
    onSubmit({
      content: content.trim(),
      ticker: ticker.trim().toUpperCase() || undefined,
      tags: parsedTags,
      sentiment,
      imageUrl: imageUrl || undefined,
    })

    // Reset form
    setContent('')
    setTicker('')
    setTags('')
    setSentiment(null)
    setImageUrl(null)
    setImagePreview(null)

    toast.success(t('insightPosted'), {
      description: t('insightNowLive'),
    })

    onClose()
  }, [content, sentiment, ticker, tags, imageUrl, onSubmit, onClose, t])

  // Keyboard shortcut: Cmd/Ctrl + Enter to post
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handlePost()
      }
    }

    if (open) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handlePost])

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('invalidFileType'), {
        description: t('uploadImageFile'),
      })
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('fileTooLarge'), {
        description: t('uploadSmallerImage'),
      })
      return
    }

    // Convert to base64 for preview
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      setImageUrl(base64)
      setImagePreview(base64)
    }
    reader.readAsDataURL(file)
  }

  const removeImage = () => {
    setImageUrl(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[90vh] w-full sm:max-w-md">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between pb-6 border-b border-white/[0.06]">
          <SheetTitle className="text-xl font-bold text-white">
            {t('createInsight')}
          </SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-8 w-8 transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto py-6 px-6 space-y-5">
          {/* Content Textarea */}
          <div className="space-y-2">
            <Label htmlFor="content" className="text-neutral-300">
              {t('insightContent')} <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="content"
              placeholder={t('shareInsightPlaceholder')}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 min-h-[120px] focus:border-emerald-500/50 focus:ring-emerald-500/20"
              maxLength={1000}
            />
            <p className="text-xs text-neutral-500 text-right font-mono">
              {t('charactersCount', { count: content.length, max: 1000 })}
            </p>
          </div>

          {/* Ticker Symbol */}
          <div className="space-y-2">
            <Label htmlFor="ticker" className="text-neutral-300">
              {t('tickerSymbol')}
            </Label>
            <Input
              id="ticker"
              placeholder={t('tickerPlaceholder')}
              value={ticker}
              onChange={(e) => {
                // Auto-uppercase ticker symbols
                const value = e.target.value.toUpperCase()
                setTicker(value)
              }}
              className="bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 font-mono focus:border-emerald-500/50 focus:ring-emerald-500/20"
              maxLength={10}
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags" className="text-neutral-300">
              {t('tags')}{' '}
              <span className="text-neutral-500 text-xs">
                {t('commaSeparated')}
              </span>
            </Label>
            <Input
              id="tags"
              placeholder={t('tagsPlaceholder')}
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="bg-neutral-900/80 border-white/[0.08] text-neutral-100 placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
            />
          </div>

          {/* Sentiment Selector */}
          <div className="space-y-2">
            <Label className="text-neutral-300">
              {t('sentiment')} <span className="text-red-400">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {/* Bullish */}
              <button
                type="button"
                onClick={() => setSentiment('bullish')}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-lg border-2 transition-all',
                  sentiment === 'bullish'
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-white/[0.08] bg-neutral-900/50 hover:border-white/[0.15]'
                )}
              >
                <TrendingUp
                  className={cn(
                    'w-6 h-6',
                    sentiment === 'bullish'
                      ? 'text-emerald-400'
                      : 'text-neutral-400'
                  )}
                />
                <span
                  className={cn(
                    'text-sm font-medium',
                    sentiment === 'bullish'
                      ? 'text-emerald-400'
                      : 'text-neutral-400'
                  )}
                >
                  {t('bullish')}
                </span>
              </button>

              {/* Neutral */}
              <button
                type="button"
                onClick={() => setSentiment('neutral')}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-lg border-2 transition-all',
                  sentiment === 'neutral'
                    ? 'border-neutral-400 bg-neutral-400/10'
                    : 'border-white/[0.08] bg-neutral-900/50 hover:border-white/[0.15]'
                )}
              >
                <Minus
                  className={cn(
                    'w-6 h-6',
                    sentiment === 'neutral'
                      ? 'text-neutral-300'
                      : 'text-neutral-400'
                  )}
                />
                <span
                  className={cn(
                    'text-sm font-medium',
                    sentiment === 'neutral'
                      ? 'text-neutral-300'
                      : 'text-neutral-400'
                  )}
                >
                  {t('neutral')}
                </span>
              </button>

              {/* Bearish */}
              <button
                type="button"
                onClick={() => setSentiment('bearish')}
                className={cn(
                  'flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-lg border-2 transition-all',
                  sentiment === 'bearish'
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-white/[0.08] bg-neutral-900/50 hover:border-white/[0.15]'
                )}
              >
                <TrendingDown
                  className={cn(
                    'w-6 h-6',
                    sentiment === 'bearish'
                      ? 'text-red-400'
                      : 'text-neutral-400'
                  )}
                />
                <span
                  className={cn(
                    'text-sm font-medium',
                    sentiment === 'bearish'
                      ? 'text-red-400'
                      : 'text-neutral-400'
                  )}
                >
                  {t('bearish')}
                </span>
              </button>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label className="text-neutral-300">
              {t('chartImage')}{' '}
              <span className="text-neutral-500 text-xs">{t('optional')}</span>
            </Label>

            {imagePreview ? (
              <div className="relative rounded-lg overflow-hidden border border-white/[0.08] bg-neutral-900/50">
                <img
                  src={imagePreview}
                  alt={t('preview')}
                  className="w-full h-auto max-h-64 object-contain"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-black/80 hover:bg-black text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-12 rounded-lg border-2 border-dashed border-white/[0.08] bg-neutral-900/30 hover:border-white/[0.15] hover:bg-neutral-900/50 transition-all flex flex-col items-center justify-center gap-2 text-neutral-400 hover:text-neutral-300"
              >
                <ImagePlus className="w-8 h-8" />
                <span className="text-sm">{t('clickToUploadImage')}</span>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 pt-4 px-6 border-t border-white/[0.06] space-y-2">
          <Button
            onClick={handlePost}
            className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold shadow-lg shadow-emerald-500/25"
          >
            {t('postInsight')}
          </Button>
          <p className="text-xs text-center text-neutral-500 font-mono">
            {t('cmdEnterToPost')}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  )
}
